import { TableColumn, TableRow, TextField } from 'react-md'
import { formatCurrency } from 'util/formatStrings'
import type { FieldArrayWithId, UseFieldArrayUpdate } from 'react-hook-form'
import { useFormContext, useWatch } from 'react-hook-form'
import { PaymentProductBillingBasis } from '@viilu-web/shared/enums/finance'
import type { LoanCreateFormInputs } from './validation'
import { exhaustiveMatchingGuard } from '@viilu-web/shared/util/exhaustiveMatchingGuard'

type LoansTableRowProps = {
	sharegroup: FieldArrayWithId<
		LoanCreateFormInputs,
		'selectableSharegroups',
		'id'
	>
	onUpdateSelectableSharegroups: UseFieldArrayUpdate<
		LoanCreateFormInputs,
		'selectableSharegroups'
	>
	index: number
	totalSelectedUnits: number
}

const LoanApartmentShareGroupsTableRow = (props: LoansTableRowProps) => {
	const {
		sharegroup,
		onUpdateSelectableSharegroups,
		index,
		totalSelectedUnits,
	} = props
	const { control } = useFormContext<LoanCreateFormInputs>()

	const [billingBasis, amountDrawn] = useWatch({
		control,
		name: ['billingBasis', 'amountDrawn'],
	})

	const onUnitChange = (value: number) => {
		let sharegroupWithUnitChange = sharegroup
		switch (billingBasis) {
			case PaymentProductBillingBasis.AREA: {
				sharegroupWithUnitChange = {
					...sharegroupWithUnitChange,
					billingBasisSquares: {
						...sharegroupWithUnitChange.billingBasisSquares,
						amount: value,
					},
				}
				break
			}
			case PaymentProductBillingBasis.AREA_ADJUSTED: {
				sharegroupWithUnitChange = {
					...sharegroupWithUnitChange,
					billingBasisWeightedSquares: {
						...sharegroupWithUnitChange.billingBasisWeightedSquares,
						amount: value,
					},
				}
				break
			}
			case PaymentProductBillingBasis.SHARE_COUNT: {
				sharegroupWithUnitChange = {
					...sharegroupWithUnitChange,
					billingBasisShares: {
						...sharegroupWithUnitChange.billingBasisShares,
						amount: value,
					},
				}
				break
			}
			case PaymentProductBillingBasis.RESIDENT_COUNT: {
				sharegroupWithUnitChange = {
					...sharegroupWithUnitChange,
					billingBasisResidents: {
						...sharegroupWithUnitChange.billingBasisResidents,
						amount: value,
					},
				}
				break
			}
			case PaymentProductBillingBasis.PIECES: {
				sharegroupWithUnitChange = {
					...sharegroupWithUnitChange,
					billingBasisPieces: {
						...sharegroupWithUnitChange.billingBasisPieces,
						amount: value,
					},
				}
				break
			}
			default: {
				exhaustiveMatchingGuard(billingBasis)
				break
			}
		}
		onUpdateSelectableSharegroups(index, {
			...sharegroupWithUnitChange,
		})
	}

	const renderUnitsAndShareAmount = () => {
		let units = 0
		let locked = false
		switch (billingBasis) {
			case PaymentProductBillingBasis.AREA: {
				units = sharegroup.billingBasisSquares.amount || 0
				if (sharegroup?.billingBasisSquares.locked === true) {
					locked = true
				}
				break
			}
			case PaymentProductBillingBasis.AREA_ADJUSTED: {
				units = sharegroup.billingBasisWeightedSquares.amount || 0
				if (sharegroup?.billingBasisWeightedSquares.locked) {
					locked = true
				}
				break
			}
			case PaymentProductBillingBasis.SHARE_COUNT: {
				units = sharegroup.billingBasisShares.amount || 0
				if (sharegroup?.billingBasisShares.locked) {
					locked = true
				}
				break
			}
			case PaymentProductBillingBasis.RESIDENT_COUNT: {
				units = sharegroup.billingBasisResidents.amount || 0
				if (sharegroup?.billingBasisResidents.locked) {
					locked = true
				}
				break
			}
			case PaymentProductBillingBasis.PIECES: {
				units = sharegroup.billingBasisPieces.amount || 0
				if (sharegroup?.billingBasisPieces.locked) {
					locked = true
				}
				break
			}
			default: {
				exhaustiveMatchingGuard(billingBasis)
				break
			}
		}
		const loanShareAmount =
			sharegroup.selected && totalSelectedUnits > 0
				? (units / totalSelectedUnits) * amountDrawn
				: 0

		// Conditionally rendering inputs, we need to make it uncontrolled
		return (
			<>
				<TableColumn>
					{!locked && sharegroup.selected ? (
						<TextField
							id={`sharegroup-units-${billingBasis}-${sharegroup.id}`}
							key={`${billingBasis}-${sharegroup.id}`}
							type="number"
							defaultValue={units}
							onBlur={(e) => {
								// react-md TextField stores value in this attribute
								const val = e.target['valueAsNumber']
								onUnitChange(Number(val))
								e.target.blur()
							}}
							min={1}
						/>
					) : (
						<>{units}</>
					)}
				</TableColumn>
				<TableColumn>{formatCurrency(loanShareAmount)}</TableColumn>
			</>
		)
	}

	return (
		<TableRow
			key={sharegroup.id}
			onCheckboxClick={() => {
				onUpdateSelectableSharegroups(index, {
					...sharegroup,
					selected: !sharegroup.selected,
				})
			}}
			selected={sharegroup.selected}
		>
			<TableColumn>{sharegroup.identifier}</TableColumn>
			{renderUnitsAndShareAmount()}
		</TableRow>
	)
}

export default LoanApartmentShareGroupsTableRow
